// ======================================================================
// FILE: src/modules/wms/services/wmsEscalationService.js
// MODULE: Work Management System (WMS)
// FEATURE: Escalation Management API Service
//
// PURPOSE:
//   Frontend API client for Escalation Management
//
// BACKEND:
//   /api/wms/escalation-policies
// ======================================================================

import axiosClient from "../../../api/axiosClient";

/**
 * ----------------------------------------------------------------------
 * LIST ESCALATION POLICIES
 * ----------------------------------------------------------------------
 * Backend:
 * GET /api/wms/escalation-policies
 */
async function listEscalationPolicies(
  params = {}
) {

  const res = await axiosClient.get(
    "/api/wms/escalation-policies",
    { params }
  );

  const payload = res.data?.data || {};

  return {
    items: payload.data || [],
    pagination: payload.pagination || {},
  };
}

/**
 * ----------------------------------------------------------------------
 * GET ESCALATION POLICY
 * ----------------------------------------------------------------------
 * Backend:
 * GET /api/wms/escalation-policies/:id
 */
async function getEscalationPolicy(
  policyId
) {

  const res = await axiosClient.get(
    `/api/wms/escalation-policies/${policyId}`
  );

  return res.data.data;
}

/**
 * ----------------------------------------------------------------------
 * CREATE ESCALATION POLICY
 * ----------------------------------------------------------------------
 * Backend:
 * POST /api/wms/escalation-policies
 */
async function createEscalationPolicy(
  payload
) {

  const res = await axiosClient.post(
    "/api/wms/escalation-policies",
    payload
  );

  return res.data.data;
}

/**
 * ----------------------------------------------------------------------
 * UPDATE ESCALATION POLICY
 * ----------------------------------------------------------------------
 * Backend:
 * PUT /api/wms/escalation-policies/:id
 */
async function updateEscalationPolicy(
  policyId,
  payload
) {

  const res = await axiosClient.put(
    `/api/wms/escalation-policies/${policyId}`,
    payload
  );

  return res.data.data;
}

/**
 * ----------------------------------------------------------------------
 * DELETE ESCALATION POLICY
 * ----------------------------------------------------------------------
 * Backend:
 * DELETE /api/wms/escalation-policies/:id
 */
async function deleteEscalationPolicy(
  policyId
) {

  const res = await axiosClient.delete(
    `/api/wms/escalation-policies/${policyId}`
  );

  return res.data;
}

/**
 * ----------------------------------------------------------------------
 * GET ESCALATION LEVELS
 * ----------------------------------------------------------------------
 * Backend:
 * GET /api/wms/escalation-policies/:id/levels
 */
async function getLevels(
  policyId
) {

  const res = await axiosClient.get(
    `/api/wms/escalation-policies/${policyId}/levels`
  );

  return res.data.data || [];
}

/**
 * ----------------------------------------------------------------------
 * CREATE ESCALATION LEVEL
 * ----------------------------------------------------------------------
 * Backend:
 * POST /api/wms/escalation-policies/:id/levels
 */
async function createLevel(
  policyId,
  payload
) {

  const res = await axiosClient.post(
    `/api/wms/escalation-policies/${policyId}/levels`,
    payload
  );

  return res.data.data;
}

/**
 * ----------------------------------------------------------------------
 * UPDATE ESCALATION LEVEL
 * ----------------------------------------------------------------------
 * Backend:
 * PUT /api/wms/escalation-policies/:policyId/levels/:levelId
 */
async function updateLevel(
  policyId,
  levelId,
  payload
) {

  const res = await axiosClient.put(
    `/api/wms/escalation-policies/${policyId}/levels/${levelId}`,
    payload
  );

  return res.data.data;
}

/**
 * ----------------------------------------------------------------------
 * DELETE ESCALATION LEVEL
 * ----------------------------------------------------------------------
 * Backend:
 * DELETE /api/wms/escalation-policies/:policyId/levels/:levelId
 */
async function deleteLevel(
  policyId,
  levelId
) {

  const res = await axiosClient.delete(
    `/api/wms/escalation-policies/${policyId}/levels/${levelId}`
  );

  return res.data;
}

/**
 * ----------------------------------------------------------------------
 * GET ESCALATION RECIPIENTS
 * ----------------------------------------------------------------------
 * Backend:
 * GET /api/wms/escalation-policies/levels/:levelId/recipients
 */
async function getRecipients(
  levelId
) {

  const res = await axiosClient.get(
    `/api/wms/escalation-policies/levels/${levelId}/recipients`
  );

  return res.data.data || [];
}

/**
 * ----------------------------------------------------------------------
 * CREATE ESCALATION RECIPIENT
 * ----------------------------------------------------------------------
 * Backend:
 * POST /api/wms/escalation-policies/levels/:levelId/recipients
 */
async function createRecipient(
  levelId,
  payload
) {

  const res = await axiosClient.post(
    `/api/wms/escalation-policies/levels/${levelId}/recipients`,
    payload
  );

  return res.data.data;
}

/**
 * ----------------------------------------------------------------------
 * DELETE ESCALATION RECIPIENT
 * ----------------------------------------------------------------------
 * Backend:
 * DELETE /api/wms/escalation-policies/levels/:levelId/recipients/:recipientId
 */
async function deleteRecipient(
  levelId,
  recipientId
) {

  const res = await axiosClient.delete(
    `/api/wms/escalation-policies/levels/${levelId}/recipients/${recipientId}`
  );

  return res.data;
}

/**
 * ----------------------------------------------------------------------
 * EXPORT
 * ----------------------------------------------------------------------
 */

const wmsEscalationService = {

  listEscalationPolicies,
  getEscalationPolicy,

  createEscalationPolicy,
  updateEscalationPolicy,
  deleteEscalationPolicy,

  getLevels,
  createLevel,
  updateLevel,
  deleteLevel,

  getRecipients,
  createRecipient,
  deleteRecipient,
};

export default wmsEscalationService;